import React, { MouseEventHandler, KeyboardEventHandler } from 'react';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import HelpIcon from '@mui/icons-material/Help';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useDictionary from '@ui/hooks/useDictionary';
import { emptyFn } from '@core/interfaces/emptyFn';
import WarningIcon from '@mui/icons-material/Warning';

interface IClasses {
    root?: string;
    labelWrap?: string;
    label?: string;
    text?: string;
    btn?: string;
}

interface IProps {
    classes?: IClasses;
    alarmState: boolean;
    name: string;
    disabled?: boolean;
    tooltip?: string;
    onAlarmStateChange: emptyFn | MouseEventHandler<HTMLButtonElement>;
}

const Component: React.FC<IProps> = ({
    classes,
    alarmState,
    name,
    disabled,
    tooltip,
    onAlarmStateChange,
}: IProps) => {
    const getWord = useDictionary();
    let switchClass = 'switch';
    let switchLabelWrapClass = 'switch__label-wrap';
    let switchLabelClass = 'switch__label';
    let switchLabelText = 'switch__label-text';
    let switchBtn = 'switch__btn';
    if (classes) {
        const { root, labelWrap, label, text, btn } = classes;
        if (root) switchClass = root;
        if (label) switchLabelClass = label;
        if (labelWrap) switchLabelWrapClass = labelWrap;
        if (text) switchLabelText = text;
        if (btn) switchBtn = btn;
    }
    const handleKeyDown: KeyboardEventHandler<HTMLElement> = (e) => {
        const { key } = e;
        if (key === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
            const target = e.target as HTMLElement;
            target.click();
        }
    };
    const switchControl = (
        <Switch
            className={switchBtn}
            checked={alarmState}
            size="small"
            id={name}
            disabled={disabled}
            onClick={onAlarmStateChange}
            onKeyDown={handleKeyDown}
        />
    );
    const labelNode = (
        <>
            <Typography className={switchLabelText} variant="body1">
                {getWord(name)}
            </Typography>
            {tooltip && (
                <Tooltip
                    classes={{
                        popper: 'switch__adornment-tooltip-popper',
                        tooltip: 'switch__adornment-tooltip',
                    }}
                    title={
                        <Typography className="switch__tooltip-text">
                            {tooltip}
                        </Typography>
                    }
                    placement="top"
                >
                    <HelpIcon
                        className="switch__adornment-ico"
                        color="action"
                    />
                </Tooltip>
            )}
        </>
    );

    return (
        <FormGroup className={switchClass}>
            <FormControlLabel
                classes={{
                    root: switchLabelWrapClass,
                    label: switchLabelClass,
                }}
                control={switchControl}
                label={labelNode}
            />
        </FormGroup>
    );
};

const SwitchComponent = React.memo(Component);

export default SwitchComponent;
